/**
 * Turns the stacked panels rendered by the lp-tabs block into a tab strip,
 * following the WAI-ARIA tabs pattern: arrow keys, Home and End move between
 * tabs, and only the selected tab is in the tab order.
 *
 * Every panel stays in the HTML; inactive ones get the hidden attribute. A
 * link to an anchor inside a panel (for example #sizes, set on the tab) opens
 * that tab and scrolls the tab strip into view, so an in-page link such as
 * "Which size do I need?" never lands on a hidden panel. Arriving with that
 * hash in the URL does the same, and selecting a block in the theme editor
 * opens the tab that holds it.
 */
class LpTabs extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector(':scope > .lp-tabs__list');
    this.panels = [...this.querySelectorAll('[data-lp-tab]')].filter((panel) => panel.closest('lp-tabs') === this);
    if (!this.list || this.panels.length === 0) return;

    this.list.replaceChildren();
    this.tabs = this.panels.map((panel, index) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'lp-tabs__tab';
      tab.id = `${panel.id}-tab`;
      tab.textContent = panel.dataset.label;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      tab.addEventListener('click', () => this.#selectFromStrip(index));
      tab.addEventListener('keydown', (event) => this.#onKeydown(event, index));

      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.tabIndex = 0;

      this.list.append(tab);
      return tab;
    });

    this.classList.add('is-enhanced');
    const fromHash = this.#indexFromHash();
    this.select(fromHash ?? 0, { focus: false });
    if (fromHash !== null) requestAnimationFrame(() => this.scrollIntoView({ block: 'start' }));

    window.addEventListener('hashchange', this.#onHashChange);
    document.addEventListener('click', this.#onAnchorClick);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this.#onHashChange);
    document.removeEventListener('click', this.#onAnchorClick);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  select(index, { focus = true } = {}) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      this.panels[i].hidden = !selected;
    });
    if (focus) this.tabs[index].focus();
    this.tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /**
   * A tab picked from the pinned strip, partway down a long panel, would open
   * the new panel at that same depth. Bring the strip back to its resting
   * place so the new panel is read from the top.
   */
  #selectFromStrip(index) {
    // Pinned, the strip rides below the top edge of the (scrolled-away) tabs block.
    const pinned = this.list.getBoundingClientRect().top > this.getBoundingClientRect().top + 1;
    this.select(index, { focus: false });
    // After layout settles: hiding a long panel shrinks the page, and phones
    // re-anchor the scroll position during that reflow.
    if (pinned) requestAnimationFrame(() => requestAnimationFrame(() => this.scrollIntoView({ block: 'start' })));
  }

  #onKeydown(event, index) {
    const last = this.tabs.length - 1;
    const next = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    this.select(next);
  }

  #indexForId(id) {
    if (!id) return null;
    const target = document.getElementById(id);
    const index = target ? this.panels.findIndex((panel) => panel.contains(target)) : -1;
    return index === -1 ? null : index;
  }

  #onAnchorClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href^="#"]') : null;
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const id = decodeURIComponent(link.getAttribute('href').slice(1));
    const index = this.#indexForId(id);
    if (index === null) return;
    event.preventDefault();
    this.select(index, { focus: false });
    history.replaceState(null, '', `#${id}`);
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    this.tabs[index].focus({ preventScroll: true });
  };

  #indexFromHash() {
    return this.#indexForId(decodeURIComponent(location.hash.slice(1)));
  }

  #onHashChange = () => {
    const index = this.#indexFromHash();
    if (index !== null) this.select(index, { focus: false });
  };

  #onBlockSelect = (event) => {
    const index = this.panels.findIndex((panel) => panel.contains(event.target));
    if (index !== -1) this.select(index, { focus: false });
  };
}

if (!customElements.get('lp-tabs')) customElements.define('lp-tabs', LpTabs);
